/**
 * Shared content-preview primitives for encounter timeline cards.
 *
 * Used by both the full patient timeline (EncountersListPage) and the
 * compact sidebar timeline (SidebarVisitsPanel) so Forms / Notes / Files /
 * Invoices / Vitals render identically in both surfaces.
 */
import { useEffect, useRef, useState } from 'react';
import { NavIcon } from '../../../shell/shared';
import type {
  EncounterFileDetailRow,
  EncounterFormPreviewRow,
  EncounterInvoicePreviewRow,
  EncounterInvoiceItemRow,
  EncounterVisualChartRow,
  EncounterVitalsDetailRow,
  OdontogramChartRow,
} from '@helios/data-access';
import { generateStrokesOverlay, generateThumbnail } from './SavedDrawPreview';
import type { DrawingRecord } from './DrawCharting';
import type { ToothStatesMap } from './OdontogramChart';
import { useFormatMoney } from '../../../hooks/useFormatMoney';
import { useAuth } from '@helios/auth-firebase-web';
import {
  AssetImage,
  pickedFromChart,
  pickedKey,
  useCompareAssets,
  type CompareAsset,
} from './compare-asset-utils';

// ─── Limits ───

export const PREVIEW_LIMIT = 2;
export const PREVIEW_LIMIT_FOCUSED = 5;

// ─── Helpers ───

export function firstLine(body: string | null): string {
  if (!body) return '';
  const line = body.split('\n')[0].trim();
  return line.length > 90 ? `${line.slice(0, 90)}…` : line;
}

export function invoiceStatusColor(status: string | null): string {
  if (status === 'paid') return 'var(--color-success-text)';
  if (status === 'issued') return 'var(--color-info, #2563eb)';
  return 'var(--text-muted)';
}

export function isImageFile(f: EncounterFileDetailRow): boolean {
  return f.kind === 'image' || (f.mime_type ?? '').startsWith('image/');
}

export function docIconForMime(mime: string | null, filename: string): string {
  const m = (mime ?? '').toLowerCase();
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (m === 'application/pdf' || ext === 'pdf') return 'file-text';
  if (m.startsWith('text/') || ['txt', 'md', 'rtf'].includes(ext)) return 'file-text';
  if (
    ['doc', 'docx'].includes(ext) ||
    m.includes('word') ||
    m.includes('officedocument.wordprocessing')
  )
    return 'file-text';
  return 'file';
}

export function formatFormDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ─── Primitives ───

export function SectionLabel({ label, count }: { label: string; count: number }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text-placeholder)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginTop: 2,
      }}
    >
      {label} · {count}
    </div>
  );
}

export function PreviewRow({
  icon,
  main,
  right,
  rightColor,
  iconColor,
}: {
  icon: string;
  main: string;
  right?: string | null;
  rightColor?: string;
  iconColor?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 'var(--font-size-xs)',
        minWidth: 0,
      }}
    >
      <span style={{ color: iconColor ?? 'var(--text-placeholder)', display: 'inline-flex' }}>
        <NavIcon name={icon} />
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          color: 'var(--text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {main}
      </span>
      {right && (
        <span
          style={{
            flexShrink: 0,
            fontSize: 10,
            color: rightColor ?? 'var(--text-muted)',
            fontWeight: 'var(--font-weight-medium)',
          }}
        >
          {right}
        </span>
      )}
    </div>
  );
}

export interface NotePreviewData {
  title: string | null;
  body: string | null;
  author_name: string | null;
  alert_level: string | null;
  created_at?: string | null;
}

function alertColorFor(level: string | null | undefined): string | undefined {
  if (level === 'critical') return '#dc2626';
  if (level === 'warning') return '#d97706';
  if (level === 'info') return '#2563eb';
  return undefined;
}

/**
 * Full-content note preview. Unlike the single-line PreviewRow, this shows
 * the note title (if present) and the complete multi-line body with line
 * breaks preserved, so the timeline shows the same detail as the Notes tab.
 *
 * `marker`:
 *   - `'edit'` (default) — the note/edit icon, good for timeline cards.
 *   - `'dot'` — a small bullet dot, good for tighter contexts like
 *     table cells where a full icon feels heavy.
 *   - `'none'` — no marker at all.
 */
export function NotePreview({
  note,
  marker = 'edit',
}: {
  note: NotePreviewData;
  marker?: 'edit' | 'dot' | 'none';
}) {
  const iconColor = alertColorFor(note.alert_level);
  const title = note.title?.trim();
  const body = note.body?.trim();
  const hasAnyText = Boolean(title || body);
  const markerEl =
    marker === 'none' ? null : marker === 'dot' ? (
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: iconColor ?? 'var(--text-placeholder)',
          flexShrink: 0,
        }}
      />
    ) : (
      <span style={{ color: iconColor ?? 'var(--text-placeholder)', display: 'inline-flex' }}>
        <NavIcon name="edit" />
      </span>
    );
  const markerOffset = marker === 'dot' ? 12 : 20;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        fontSize: 'var(--font-size-xs)',
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          minWidth: 0,
        }}
      >
        {markerEl}
        {title ? (
          <span
            style={{
              flex: 1,
              minWidth: 0,
              color: 'var(--text)',
              fontWeight: 'var(--font-weight-medium)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </span>
        ) : !body ? (
          <span
            style={{
              flex: 1,
              minWidth: 0,
              color: 'var(--text-muted)',
              fontStyle: 'italic',
            }}
          >
            (empty note)
          </span>
        ) : (
          <span style={{ flex: 1 }} />
        )}
        {note.author_name && (
          <span
            style={{
              flexShrink: 0,
              fontSize: 10,
              color: 'var(--text-muted)',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            {note.author_name}
          </span>
        )}
      </div>
      {body && hasAnyText && (
        <div
          style={{
            marginLeft: markerOffset,
            color: 'var(--text)',
            whiteSpace: 'pre-line',
            wordBreak: 'break-word',
            lineHeight: 1.45,
          }}
        >
          {body}
        </div>
      )}
    </div>
  );
}

export function MoreLink({ count, singular }: { count: number; singular: string }) {
  return (
    <span
      style={{
        fontSize: 10,
        color: 'var(--text-placeholder)',
        marginLeft: 20,
      }}
    >
      +{count} more {singular}
      {count > 1 ? 's' : ''}
    </span>
  );
}

export function FormStatusPill({ status }: { status: string }) {
  const isDraft = status === 'draft';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 'var(--radius-full)',
        background: isDraft ? 'var(--color-warning-bg)' : 'var(--color-success-bg)',
        color: isDraft ? 'var(--color-warning-text)' : 'var(--color-success-text)',
        fontSize: 10,
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '16px',
        whiteSpace: 'nowrap',
      }}
    >
      {isDraft ? 'Draft' : 'Completed'}
    </span>
  );
}

export function EmbeddedFormRow({
  form,
  onOpen,
}: {
  form: EncounterFormPreviewRow;
  onOpen: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onOpen();
        }
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-surface)';
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 10px',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        cursor: 'pointer',
        outline: 'none',
        minWidth: 0,
      }}
    >
      <span style={{ color: 'var(--text-muted)', display: 'inline-flex', flexShrink: 0 }}>
        <NavIcon name="file-text" />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {form.form_name ?? 'Untitled Form'}
        </div>
        <div
          style={{
            fontSize: 10,
            color: 'var(--text-muted)',
            marginTop: 1,
          }}
        >
          {formatFormDate(form.completed_at ?? form.created_at)}
        </div>
      </div>
      <FormStatusPill status={form.status} />
    </div>
  );
}

export function FileThumbnail({
  file,
  onOpen,
}: {
  file: EncounterFileDetailRow;
  onOpen: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      title={file.filename}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onOpen();
        }
      }}
      style={{
        width: 52,
        height: 52,
        borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface-2, #f5f5f5)',
        overflow: 'hidden',
        flexShrink: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text-placeholder)',
        outline: 'none',
      }}
    >
      {file.url ? (
        <img
          src={file.url}
          alt={file.filename}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <NavIcon name="image" />
      )}
    </div>
  );
}

export function DocumentFileRow({
  file,
  onOpen,
}: {
  file: EncounterFileDetailRow;
  onOpen: () => void;
}) {
  const icon = docIconForMime(file.mime_type, file.filename);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onOpen();
        }
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'transparent';
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 8px 4px 2px',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--font-size-xs)',
        cursor: 'pointer',
        minWidth: 0,
        outline: 'none',
      }}
    >
      <span style={{ color: 'var(--text-placeholder)', display: 'inline-flex', flexShrink: 0 }}>
        <NavIcon name={icon} />
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          color: 'var(--text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {file.filename}
      </span>
      {file.tag && (
        <span
          style={{
            flexShrink: 0,
            fontSize: 10,
            color: 'var(--text-muted)',
            fontWeight: 'var(--font-weight-medium)',
          }}
        >
          {file.tag}
        </span>
      )}
    </div>
  );
}

export function VitalChip({ vital }: { vital: EncounterVitalsDetailRow }) {
  const value = vital.value_number != null ? String(vital.value_number) : (vital.value_text ?? '—');
  return (
    <span
      style={{
        padding: '1px 6px',
        borderRadius: 'var(--radius-xs)',
        background: 'var(--bg-surface-2, #f5f5f5)',
        fontSize: 10,
        color: 'var(--text-muted)',
        whiteSpace: 'nowrap',
      }}
    >
      {vital.name}:{' '}
      <span style={{ color: 'var(--text)', fontWeight: 'var(--font-weight-medium)' }}>
        {value}
        {vital.unit ? ` ${vital.unit}` : ''}
      </span>
    </span>
  );
}

const INVOICE_ITEM_PREVIEW_LIMIT = 3;

export function InvoicePreviewRow({
  invoice,
  items = [],
  onOpen,
}: {
  invoice: EncounterInvoicePreviewRow;
  items?: EncounterInvoiceItemRow[];
  onOpen: () => void;
}) {
  const fmt = useFormatMoney();
  const amount = invoice.grand_total == null ? '' : fmt(invoice.grand_total);
  const rightText = [invoice.status, amount].filter((x) => x).join(' · ');

  // Keep only items with a real name; preserve their original order.
  // PRD.190 — prefer snapshot/free-text fields over the legacy service_name
  // when checking for a "real name" so historical lines bound to inventory
  // items still appear even if their legacy service_name is empty.
  const namedItems = items.filter((i) =>
    (i.description_snapshot ?? i.free_text_name ?? i.service_name ?? '').trim(),
  );
  const visibleItems = namedItems.slice(0, INVOICE_ITEM_PREVIEW_LIMIT);
  const hiddenCount = namedItems.length - visibleItems.length;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onOpen();
        }
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-surface)';
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '6px 10px',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        cursor: 'pointer',
        outline: 'none',
        minWidth: 0,
      }}
    >
      {/* Header: icon + invoice no + status/amount */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <span style={{ color: 'var(--text-muted)', display: 'inline-flex', flexShrink: 0 }}>
          <NavIcon name="credit-card" />
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {invoice.invoice_no ?? 'Invoice'}
        </span>
        {rightText && (
          <span
            style={{
              flexShrink: 0,
              fontSize: 10,
              color: invoiceStatusColor(invoice.status),
              fontWeight: 'var(--font-weight-medium)',
              whiteSpace: 'nowrap',
            }}
          >
            {rightText}
          </span>
        )}
      </div>
      {/* Items: one row per item with a right-aligned line price */}
      {visibleItems.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginLeft: 26,
          }}
        >
          {visibleItems.map((it, ii) => {
            const name = (
              it.description_snapshot ??
              it.free_text_name ??
              it.service_name ??
              ''
            ).trim();
            const qty = it.qty ?? 1;
            const unit = it.unit_price ?? 0;
            const lineTotal = qty * unit;
            return (
              <div
                key={`${it.invoice_id}:${ii}`}
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 6,
                  fontSize: 10,
                  color: 'var(--text-muted)',
                  lineHeight: 1.5,
                }}
              >
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    color: 'var(--text)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {qty > 1 ? `${name} ×${qty}` : name}
                </span>
                <span
                  style={{
                    flexShrink: 0,
                    fontVariantNumeric: 'tabular-nums',
                    color: 'var(--text-muted)',
                  }}
                >
                  {lineTotal == null ? '' : fmt(lineTotal)}
                </span>
              </div>
            );
          })}
          {hiddenCount > 0 && (
            <span style={{ fontSize: 10, color: 'var(--text-placeholder)', marginTop: 2 }}>
              +{hiddenCount} more item{hiddenCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Thumbnail card for a saved charting record (`encounter_visual_charts`).
 * - Before/after pair → split half/half thumbnail using file URLs from
 *   `filesById` (lookup map provided by the parent so we don't query
 *   per-card).
 * - Drawing → client-rendered Konva thumbnail from `drawing_data`. For
 *   drawings layered over a Firebase image, the base image is rendered as
 *   a regular `<img>` with the strokes painted on a transparent overlay.
 *
 * Sized to match `FileThumbnail` (52×52) so saved chartings sit alongside
 * file thumbnails in a single visual row.
 */
export function DrawingPreviewRow({
  chart,
  filesById,
  onOpen,
}: {
  chart: EncounterVisualChartRow;
  filesById?: Map<string, { url: string | null }>;
  onOpen?: () => void;
}) {
  const isBeforeAfter = chart.chart_type === 'before_after';
  const interactive = Boolean(onOpen);
  const title =
    (chart.label && chart.label.trim()) || (isBeforeAfter ? 'Before / After' : 'Drawing');

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      title={title}
      onClick={
        interactive
          ? (e) => {
              e.stopPropagation();
              onOpen?.();
            }
          : undefined
      }
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onOpen?.();
              }
            }
          : undefined
      }
      style={{
        position: 'relative',
        width: 52,
        height: 52,
        borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border)',
        background: '#fff',
        overflow: 'hidden',
        flexShrink: 0,
        cursor: interactive ? 'pointer' : 'default',
        outline: 'none',
      }}
    >
      {isBeforeAfter ? (
        <BeforeAfterThumbBody chart={chart} filesById={filesById} />
      ) : (
        <DrawingThumbBody chart={chart} filesById={filesById} />
      )}
      <span
        style={{
          position: 'absolute',
          bottom: 1,
          left: 1,
          padding: '0 4px',
          borderRadius: 2,
          background: 'rgba(0,0,0,0.6)',
          color: '#fff',
          fontSize: 8,
          fontWeight: 600,
          letterSpacing: '0.2px',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        {isBeforeAfter ? 'B/A' : 'Draw'}
      </span>
    </div>
  );
}

function BeforeAfterThumbBody({
  chart,
}: {
  chart: EncounterVisualChartRow;
  /** Legacy: photo-id -> file row. Unused now; kept for caller compatibility. */
  filesById?: Map<string, { url: string | null }>;
}) {
  // Resolve both halves via the shared compare-asset pipeline so drawing-side
  // refs (PRD.235) render the actual drawing thumbnail, not a blank fallback.
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const { assetByKey } = useCompareAssets(chart.patient_id, chart.encounter_id, orgId);

  const beforeKey = pickedKey(pickedFromChart(chart, 'before'));
  const afterKey = pickedKey(pickedFromChart(chart, 'after'));
  const beforeAsset = beforeKey ? (assetByKey.get(beforeKey) ?? null) : null;
  const afterAsset = afterKey ? (assetByKey.get(afterKey) ?? null) : null;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        background: 'var(--bg-surface-2, #f5f5f5)',
      }}
    >
      <ThumbHalf asset={beforeAsset} fallback="B" />
      <ThumbHalf asset={afterAsset} fallback="A" />
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: '50%',
          width: 1,
          background: 'rgba(255,255,255,0.7)',
        }}
      />
    </div>
  );
}

function ThumbHalf({ asset, fallback }: { asset: CompareAsset | null; fallback: string }) {
  if (!asset) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-placeholder)',
          fontSize: 10,
          fontWeight: 600,
        }}
      >
        {fallback}
      </div>
    );
  }
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <AssetImage
        asset={asset}
        alt={fallback}
        objectFit={asset.source === 'drawing' ? 'contain' : 'cover'}
      />
    </div>
  );
}

function DrawingThumbBody({
  chart,
  filesById,
}: {
  chart: EncounterVisualChartRow;
  filesById?: Map<string, { url: string | null }>;
}) {
  const record: DrawingRecord | null = (() => {
    if (!chart.drawing_data) return null;
    try {
      return JSON.parse(chart.drawing_data) as DrawingRecord;
    } catch {
      return null;
    }
  })();

  const baseImageUrl =
    record?.baseType === 'image' && record.baseImageId
      ? (filesById?.get(record.baseImageId)?.url ?? null)
      : null;

  const isFirebaseImage =
    record?.baseType === 'image' &&
    !!baseImageUrl &&
    baseImageUrl.includes('firebasestorage.googleapis.com');

  const [strokesUrl, setStrokesUrl] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const generatedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!record) return;
    const key = `${record.baseType}:${baseImageUrl ?? ''}:${record.strokes.length}`;
    if (generatedForRef.current === key) return;
    generatedForRef.current = key;

    if (isFirebaseImage) {
      setThumbnailUrl(null);
      generateStrokesOverlay(record)
        .then(setStrokesUrl)
        .catch(() => {});
    } else {
      setStrokesUrl(null);
      generateThumbnail(record, baseImageUrl)
        .then(setThumbnailUrl)
        .catch(() => {});
    }
  }, [record, baseImageUrl, isFirebaseImage]);

  if (!record) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-placeholder)',
        }}
      >
        <NavIcon name="edit" />
      </div>
    );
  }

  const ready = isFirebaseImage ? !!strokesUrl : !!thumbnailUrl;

  if (!ready) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-placeholder)',
          fontSize: 9,
        }}
      >
        …
      </div>
    );
  }

  if (isFirebaseImage) {
    return (
      <>
        <img
          src={baseImageUrl!}
          alt="Base"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
        <img
          src={strokesUrl!}
          alt="Strokes"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </>
    );
  }

  return (
    <img
      src={thumbnailUrl!}
      alt="Drawing"
      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
    />
  );
}

// ─── Tooth chart thumbnail (odontogram) ───

const ADULT_UPPER = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
const ADULT_LOWER = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];

const TOOTH_CONDITION_COLORS: Record<string, string> = {
  caries: '#dc2626',
  composite: '#3b82f6',
  amalgam: '#6b7280',
  recurrent_caries: '#f97316',
};

const TOOTH_CROWN_COLORS: Record<string, string> = {
  ceramic: '#e2e8f0',
  pfm: '#fbbf24',
  gold: '#f59e0b',
  metal: '#9ca3af',
};

function parseToothStates(raw: string | null): ToothStatesMap {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as ToothStatesMap;
  } catch {
    return {};
  }
}

function toothFill(state: ToothStatesMap[string] | undefined): {
  fill: string;
  isMissing: boolean;
} {
  if (!state) return { fill: '#e5e7eb', isMissing: false };
  if (state.flags?.includes('missing')) return { fill: '#fecaca', isMissing: true };
  if (state.conditions && state.conditions.length > 0) {
    const code = state.conditions[0].code;
    return { fill: TOOTH_CONDITION_COLORS[code] ?? '#94a3b8', isMissing: false };
  }
  if (state.crown) {
    return { fill: TOOTH_CROWN_COLORS[state.crown] ?? '#94a3b8', isMissing: false };
  }
  return { fill: '#e5e7eb', isMissing: false };
}

/**
 * Compact 52×52 thumbnail for a saved tooth chart (`odontogram_charts`).
 * Renders a mini-odontogram of the 32 adult teeth (16 upper / 16 lower),
 * each as a small dot colored by its condition / crown / missing state.
 * Click opens the encounter's Charting tab.
 */
export function ToothChartPreviewRow({
  chart,
  onOpen,
}: {
  chart: OdontogramChartRow;
  onOpen?: () => void;
}) {
  const states = parseToothStates(chart.tooth_states);
  const interactive = Boolean(onOpen);
  const title = `Part Chart${chart.status ? ` (${chart.status})` : ''}`;

  // Layout constants for 52×52 card
  const cardSize = 52;
  const cols = 16;
  const cellW = cardSize / cols; // ~3.25
  const dotR = cellW / 2 - 0.4;
  const upperY = cardSize * 0.35;
  const lowerY = cardSize * 0.65;

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      title={title}
      onClick={
        interactive
          ? (e) => {
              e.stopPropagation();
              onOpen?.();
            }
          : undefined
      }
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onOpen?.();
              }
            }
          : undefined
      }
      style={{
        position: 'relative',
        width: cardSize,
        height: cardSize,
        borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border)',
        background: '#fff',
        overflow: 'hidden',
        flexShrink: 0,
        cursor: interactive ? 'pointer' : 'default',
        outline: 'none',
      }}
    >
      <svg
        width={cardSize}
        height={cardSize}
        viewBox={`0 0 ${cardSize} ${cardSize}`}
        style={{ display: 'block' }}
      >
        {/* Jaw separator */}
        <line
          x1={2}
          y1={cardSize / 2}
          x2={cardSize - 2}
          y2={cardSize / 2}
          stroke="#e5e7eb"
          strokeWidth={0.5}
        />
        {ADULT_UPPER.map((num, i) => {
          const { fill, isMissing } = toothFill(states[String(num)]);
          const cx = i * cellW + cellW / 2;
          return (
            <g key={`u-${num}`}>
              <circle cx={cx} cy={upperY} r={dotR} fill={fill} stroke="#cbd5e1" strokeWidth={0.3} />
              {isMissing && (
                <line
                  x1={cx - dotR}
                  y1={upperY - dotR}
                  x2={cx + dotR}
                  y2={upperY + dotR}
                  stroke="#dc2626"
                  strokeWidth={0.6}
                />
              )}
            </g>
          );
        })}
        {ADULT_LOWER.map((num, i) => {
          const { fill, isMissing } = toothFill(states[String(num)]);
          const cx = i * cellW + cellW / 2;
          return (
            <g key={`l-${num}`}>
              <circle cx={cx} cy={lowerY} r={dotR} fill={fill} stroke="#cbd5e1" strokeWidth={0.3} />
              {isMissing && (
                <line
                  x1={cx - dotR}
                  y1={lowerY - dotR}
                  x2={cx + dotR}
                  y2={lowerY + dotR}
                  stroke="#dc2626"
                  strokeWidth={0.6}
                />
              )}
            </g>
          );
        })}
      </svg>
      <span
        style={{
          position: 'absolute',
          bottom: 1,
          left: 1,
          padding: '0 4px',
          borderRadius: 2,
          background: 'rgba(0,0,0,0.6)',
          color: '#fff',
          fontSize: 8,
          fontWeight: 600,
          letterSpacing: '0.2px',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        Tooth
      </span>
    </div>
  );
}
